import { AsyncFeedback, LoadingState } from '../../components/feedback/LoadingState'
import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { pageTheme,titleTheme,cardTheme } from '../../components/consoleTheme'
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CreditCard, FileText, Receipt, Plus, Trash2, Check, X, AlertCircle, Info, Star, Calendar } from 'lucide-react';

function getToken() {
  return sessionStorage.getItem('token') || localStorage.getItem('token');
}

async function authFetch(url, opts = {}) {
  const token = getToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const res = await fetch(url, { ...opts, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

function fmtKRW(n) {
  return Number(n || 0).toLocaleString('ko-KR') + '원';
}

function fmtTok(n) {
  return Number(n || 0).toLocaleString('ko-KR', { maximumFractionDigits: 2 });
}

const STATUS_LABEL = {
  pending: '미결제', paid: '결제완료', overdue: '연체', cancelled: '취소'
};

export default function BillingPage() {
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [toast, setToast] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [txs, setTxs] = useState([]);
  const [topupAmt, setTopupAmt] = useState('');

  const showToast = (msg, kind = 'success') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3000);
  };

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const [s, i, m] = await Promise.all([
        authFetch('/api/billing/summary'),
        authFetch('/api/billing/invoices'),
        authFetch('/api/billing/payment-methods')
      ]);
      setSummary(s.summary);
      setInvoices(i.invoices || []);
      setMethods(m.methods || []);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };
  const loadWallet = async () => {
    try {
      const [w, t] = await Promise.all([
        authFetch('/api/billing/wallet'),
        authFetch('/api/billing/wallet/transactions'),
      ]);
      setWallet(w); setTxs(t.transactions || []);
    } catch (e) { /* 지갑 미사용 환경은 무시 */ }
  };
  const topup = async (amt) => {
    const n = Number(amt != null ? amt : topupAmt);
    if (!(n > 0)) { showToast('충전량을 입력하세요.', 'error'); return; }
    try {
      await authFetch('/api/billing/wallet/topup', { method: 'POST', body: JSON.stringify({ amount: n }) });
      setTopupAmt(''); await loadWallet();
      showToast(`${n.toLocaleString('ko-KR')} SKY 충전되었습니다.`);
    } catch (e) { showToast(e.message, 'error'); }
  };
  useEffect(() => { load(); loadWallet(); }, []);

  const handleSetDefault = async (id) => {
    try {
      await authFetch(`/api/billing/payment-methods/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_default: true })
      });
      await load();
      showToast('기본 결제수단으로 설정되었습니다.');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`"${label}" 결제수단을 삭제하시겠습니까?`)) return;
    try {
      await authFetch(`/api/billing/payment-methods/${id}`, { method: 'DELETE' });
      await load();
      showToast('삭제되었습니다.');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  if (loading) return <Wrap><LoadingState label="불러오는 중…" /></Wrap>;
  if (err && !summary) return <Wrap><AsyncFeedback error={err} onRetry={load}/></Wrap>;

  return (
    <Wrap>
      <PageTitle><CreditCard size={22} /> 결제 / 사용량</PageTitle>

      {toast && <Toast kind={toast.kind}>
        {toast.kind === 'error' ? <X size={16} /> : <Check size={16} />}
        {toast.msg}
      </Toast>}

      {/* 토큰 지갑 — 실사용량 차감 */}
      {wallet && (
        <WalletCard low={wallet.low}>
          <WalletTop>
            <div>
              <WLabel>내 토큰 잔액</WLabel>
              <WBalance>{fmtTok(wallet.balance)} <span>{wallet.token?.symbol || 'SKY'}</span></WBalance>
              <WMeta>
                시간당 사용요율 <b>{Number(wallet.hourly_rate || 0).toFixed(2)}</b> {wallet.token?.symbol}/h
                {wallet.last_charged > 0 && <em> · 방금 {fmtTok(wallet.last_charged)} 차감됨</em>}
              </WMeta>
            </div>
            <Topup>
              <input type="number" min="1" placeholder="충전량"
                value={topupAmt} onChange={(e) => setTopupAmt(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && topup()} />
              <TopupBtn onClick={() => topup()}>충전</TopupBtn>
              {[100, 500, 1000].map((q) => <QuickBtn key={q} onClick={() => topup(q)}>+{q}</QuickBtn>)}
            </Topup>
          </WalletTop>
          {wallet.low && <WLow><AlertCircle size={15} /> 잔액이 부족합니다. 토큰을 충전해 주세요.</WLow>}
          {wallet.resources && (
            <WRes>
              <span>보유 리소스 (차감 대상)</span>
              <ul>
                <li>컴퓨트 {wallet.resources.compute}</li>
                <li>VPC 피어 {wallet.resources.vpc_peer}</li>
                <li>스토리지 공유 {wallet.resources.storage_share}</li>
                <li>레지스트리 {wallet.resources.registry_ns}</li>
                <li>DB {wallet.resources.db_conn}</li>
                <li>함수 {wallet.resources.serverless_fn}</li>
              </ul>
            </WRes>
          )}
        </WalletCard>
      )}

      {/* 토큰 거래내역 */}
      {wallet && (
        <Card>
          <CardHeader><CardTitle><Receipt size={18} /> 토큰 거래내역</CardTitle></CardHeader>
          {txs.length === 0 ? <Empty>거래내역이 없습니다.</Empty> : (
            <UsageTable>
              <thead><tr><th>구분</th><th>내용</th><th style={{ textAlign: 'right' }}>변동</th><th style={{ textAlign: 'right' }}>잔액</th><th style={{ textAlign: 'right' }}>일시</th></tr></thead>
              <tbody>
                {txs.map((t) => (
                  <tr key={t.tx_id}>
                    <td>{t.kind === 'topup' ? '충전' : t.kind === 'usage' ? '사용' : '조정'}</td>
                    <td>{t.description}</td>
                    <td style={{ textAlign: 'right', color: t.amount < 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                      {t.amount < 0 ? '' : '+'}{fmtTok(t.amount)}
                    </td>
                    <td style={{ textAlign: 'right' }}>{fmtTok(t.balance_after)}</td>
                    <td style={{ textAlign: 'right', color: '#94a3b8', fontSize: '.8rem' }}>{new Date(t.created_at).toLocaleString('ko-KR')}</td>
                  </tr>
                ))}
              </tbody>
            </UsageTable>
          )}
        </Card>
      )}

      {summary?.notice && (
        <NoticeCard>
          <Info size={18} />
          <div>
            <strong>안내</strong>
            <p>{summary.notice}</p>
          </div>
        </NoticeCard>
      )}

      {/* 이번 달 예상 */}
      <BillSummary>
        <SummaryHeader>
          <div>
            <SummaryLabel>이번 달 예상 청구 ({summary?.period})</SummaryLabel>
            <SummaryAmount>{fmtKRW(summary?.total)}</SummaryAmount>
          </div>
          <NextDate>
            <Calendar size={14} />
            다음 결제일 {summary?.next_billing_date}
          </NextDate>
        </SummaryHeader>
        <UsageTable>
          <thead>
            <tr><th>항목</th><th>수량</th><th>단가</th><th style={{ textAlign: 'right' }}>금액</th></tr>
          </thead>
          <tbody>
            {summary?.usage.map((u, i) => (
              <tr key={i}>
                <td>{u.description}</td>
                <td>{u.quantity} {u.unit}</td>
                <td>{fmtKRW(u.unit_price)} / {u.unit}</td>
                <td style={{ textAlign: 'right' }}>{fmtKRW(u.amount)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={3} style={{ textAlign: 'right', color: '#6b7280' }}>소계</td>
              <td style={{ textAlign: 'right' }}>{fmtKRW(summary?.subtotal)}</td>
            </tr>
            <tr>
              <td colSpan={3} style={{ textAlign: 'right', color: '#6b7280' }}>부가세 (10%)</td>
              <td style={{ textAlign: 'right' }}>{fmtKRW(summary?.tax)}</td>
            </tr>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={3} style={{ textAlign: 'right' }}>합계</td>
              <td style={{ textAlign: 'right' }}>{fmtKRW(summary?.total)}</td>
            </tr>
          </tbody>
        </UsageTable>
      </BillSummary>

      {/* 결제 수단 */}
      <Card>
        <CardHeader>
          <CardTitle><CreditCard size={18} /> 결제 수단</CardTitle>
          <SmallPrimaryBtn onClick={() => setShowAddMethod(true)}>
            <Plus size={14} /> 추가
          </SmallPrimaryBtn>
        </CardHeader>
        {methods.length === 0 ? (
          <Empty>등록된 결제수단이 없습니다.</Empty>
        ) : (
          <MethodList>
            {methods.map(m => (
              <MethodItem key={m.method_id}>
                <MethodIcon type={m.method_type}>
                  {m.method_type === 'card' ? <CreditCard size={20} /> : <Receipt size={20} />}
                </MethodIcon>
                <MethodBody>
                  <MethodLabel>
                    {m.label || (m.method_type === 'card' ? '신용카드' : m.method_type === 'bank' ? '계좌이체' : '가상계좌')}
                    {m.is_default && <DefaultTag><Star size={11} /> 기본</DefaultTag>}
                  </MethodLabel>
                  <MethodInfo>
                    {m.issuer && <span>{m.issuer}</span>}
                    <Mono>{m.masked_info}</Mono>
                  </MethodInfo>
                </MethodBody>
                <MethodActions>
                  {!m.is_default && (
                    <TinyBtn onClick={() => handleSetDefault(m.method_id)} title="기본으로 설정">기본 설정</TinyBtn>
                  )}
                  <TinyBtn danger onClick={() => handleDelete(m.method_id, m.label || '결제수단')}>
                    <Trash2 size={12} />
                  </TinyBtn>
                </MethodActions>
              </MethodItem>
            ))}
          </MethodList>
        )}
      </Card>

      {/* 청구서 이력 */}
      <Card>
        <CardHeader><CardTitle><FileText size={18} /> 청구서 이력</CardTitle></CardHeader>
        {invoices.length === 0 ? (
          <Empty>발행된 청구서가 없습니다. (월말에 자동 발행됩니다)</Empty>
        ) : (
          <InvoiceTable>
            <thead>
              <tr>
                <th>청구번호</th><th>기간</th><th>발행일</th><th>금액</th><th>상태</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.invoice_id}>
                  <td><Mono>{inv.invoice_no || `#${inv.invoice_id}`}</Mono></td>
                  <td>{inv.period_start} ~ {inv.period_end}</td>
                  <td>{new Date(inv.issued_at).toLocaleDateString('ko-KR')}</td>
                  <td>{fmtKRW(inv.total_amount)}</td>
                  <td><StatusBadge status={inv.status}>{STATUS_LABEL[inv.status]}</StatusBadge></td>
                </tr>
              ))}
            </tbody>
          </InvoiceTable>
        )}
      </Card>

      {showAddMethod && (
        <AddMethodModal onClose={() => setShowAddMethod(false)} onAdded={() => { setShowAddMethod(false); load(); showToast('결제수단이 등록되었습니다.'); }} />
      )}
    </Wrap>
  );
}

// ============== 결제수단 추가 모달 ==============
function AddMethodModal({ onClose, onAdded }) {
  const [methodType, setMethodType] = useState('card');
  const [issuer, setIssuer] = useState('');
  const [last4, setLast4] = useState('');
  const [label, setLabel] = useState('');
  const [setDefault, setSetDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    if (!/^\d{3,4}$/.test(last4)) return setErr('뒷자리 3~4자리 숫자를 입력해 주세요.');
    setSaving(true);
    try {
      await authFetch('/api/billing/payment-methods', {
        method: 'POST',
        body: JSON.stringify({ method_type: methodType, label, issuer, last4, set_default: setDefault })
      });
      onAdded();
    } catch (e) {
      setErr(e.message);
    }
    setSaving(false);
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3>결제수단 추가</h3>
          <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
        </ModalHeader>
        <ModalBody>
          <NoticeBox>
            <Info size={14} /> Phase 1 은 PG 연동 없이 표시 정보만 저장합니다. 실제 결제는 추후 활성화됩니다.
          </NoticeBox>
          <Field>
            <Label>종류</Label>
            <Select value={methodType} onChange={e => setMethodType(e.target.value)}>
              <option value="card">신용/체크 카드</option>
              <option value="bank">계좌이체</option>
              <option value="virtual_account">가상계좌</option>
            </Select>
          </Field>
          <Field>
            <Label>{methodType === 'card' ? '카드사' : '은행'}</Label>
            <Input value={issuer} onChange={e => setIssuer(e.target.value)} placeholder={methodType === 'card' ? '예: 신한, 삼성, 현대' : '예: 국민, 신한'} />
          </Field>
          <Field>
            <Label>{methodType === 'card' ? '카드번호 뒷 4자리' : '계좌번호 뒷 3~4자리'}</Label>
            <Input value={last4} onChange={e => setLast4(e.target.value.replace(/[^0-9]/g, ''))} maxLength={4} placeholder="0000" />
          </Field>
          <Field>
            <Label>별칭 (선택)</Label>
            <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="예: 회사 법인카드" />
          </Field>
          <CheckRow onClick={() => setSetDefault(v => !v)}>
            <input type="checkbox" checked={setDefault} onChange={() => setSetDefault(v => !v)} />
            <span>기본 결제수단으로 설정</span>
          </CheckRow>
          {err && <ErrorBox><AlertCircle size={14} /> {err}</ErrorBox>}
        </ModalBody>
        <ModalFooter>
          <SecondaryBtn onClick={onClose} disabled={saving}>취소</SecondaryBtn>
          <PrimaryBtn onClick={submit} disabled={saving}>
            {saving ? '저장 중...' : '추가'}
          </PrimaryBtn>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
}

// ============== Styled ==============
const Wrap = styled.div`
 ${pageTheme}

  padding: 28px 36px 60px;
  max-width: 980px;
  margin: 0 auto;
  text-align: left;
  @media(max-width:768px){padding:1rem 1rem 2rem;}
`;
const PageTitle = styled.h1`
 ${titleTheme}
 &&{margin-bottom:28px;}

  font-size: 1.6rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 24px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const WalletCard = styled.div`
  background: #193c46;
  color: #fff; border-radius: 14px; padding: 22px 24px; margin-bottom: 16px;
  border: 1px solid ${(p) => (p.low ? '#f87171' : 'transparent')};
  @media(max-width:768px){padding:1rem;}
`;
const WalletTop = styled.div`display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap;`;
const WLabel = styled.div`font-size: 13px; color: #c7d2fe; margin-bottom: 6px;`;
const WBalance = styled.div`font-size: 2.4rem; font-weight: 800; line-height: 1; span { font-size: 1rem; font-weight: 600; color: #93c5fd; margin-left: 4px; }`;
const WMeta = styled.div`font-size: 12.5px; color: #cbd5e1; margin-top: 8px; b { color: #fff; } em { color: #fcd34d; font-style: normal; }`;
const Topup = styled.div`
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  input { width: 110px; padding: 8px 10px; border-radius: 7px; border: 1px solid #475569; background: #0b1220; color: #fff; outline: none; font-size: 14px; &:focus { border-color: #60a5fa; } }
`;
const TopupBtn = styled.button`padding: 8px 14px; border-radius: 7px; border: none; background: #0066FF; color: #fff; font-weight: 600; cursor: pointer; &:hover { background: #0052CC; }`;
const QuickBtn = styled.button`padding: 8px 10px; border-radius: 7px; border: 1px solid #475569; background: transparent; color: #cbd5e1; cursor: pointer; font-size: 13px; &:hover { border-color: #93c5fd; color: #fff; }`;
const WLow = styled.div`display: flex; align-items: center; gap: 6px; margin-top: 14px; padding: 8px 12px; background: rgba(248,113,113,.15); border: 1px solid #f87171; border-radius: 8px; color: #fecaca; font-size: 13px;`;
const WRes = styled.div`
  margin-top: 16px; padding-top: 14px; border-top: 1px solid rgba(148,163,184,.25);
  span { font-size: 12px; color: #94a3b8; }
  ul { display: flex; flex-wrap: wrap; gap: 8px 16px; list-style: none; margin: 8px 0 0; padding: 0; }
  li { font-size: 13px; color: #e2e8f0; }
`;
const NoticeCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 14px 18px;
  margin-bottom: 16px;
  color: #92400e;
  strong { display: block; font-size: 13px; margin-bottom: 4px; }
  p { margin: 0; font-size: 12px; line-height: 1.5; color: #78350f; }
`;
const BillSummary = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 22px 26px;
  margin-bottom: 16px;
  @media(max-width:768px){padding:1rem;}
`;
const SummaryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px;
`;
const SummaryLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 4px;
`;
const SummaryAmount = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
`;
const NextDate = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 12px;
  color: #4b5563;
`;
const Card = styled.div`
 ${cardTheme}

  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 22px;
  margin-bottom: 16px;
  @media(max-width:768px){padding:1rem;max-width:100%;overflow-x:auto;}
`;
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;
const CardTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const UsageTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th, td {
    padding: 9px 12px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
  }
  th {
    font-weight: 600;
    color: #6b7280;
    font-size: 12px;
    background: #fafbfc;
  }
  tr:last-child td { border-bottom: none; }
`;
const InvoiceTable = styled(UsageTable)``;
const Empty = styled.div`
  text-align: center;
  padding: 30px;
  color: #9ca3af;
  font-size: 13px;
`;
const SmallPrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #1d4ed8; }
`;
const MethodList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const MethodItem = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;
const MethodIcon = styled.div`
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ type }) => type === 'card' ? '#dbeafe' : '#d1fae5'};
  color: ${({ type }) => type === 'card' ? '#1e40af' : '#065f46'};
  border-radius: 8px;
`;
const MethodBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const MethodLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const DefaultTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  background: #fef3c7;
  color: #92400e;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
`;
const MethodInfo = styled.div`
  font-size: 12px;
  color: #6b7280;
  display: flex;
  gap: 8px;
  align-items: center;
`;
const MethodActions = styled.div`
  display: flex;
  gap: 4px;
`;
const TinyBtn = styled.button`
  padding: 5px 10px;
  background: white;
  color: ${({ danger }) => danger ? '#b91c1c' : '#374151'};
  border: 1px solid ${({ danger }) => danger ? '#fecaca' : '#d1d5db'};
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  &:hover { background: ${({ danger }) => danger ? '#fee2e2' : '#f3f4f6'}; }
`;
const Mono = styled.span`
  font-family: 'Cascadia Mono', Consolas, monospace;
  font-size: 12px;
`;
const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ status }) =>
    status === 'paid' ? '#d1fae5' :
    status === 'pending' ? '#fef3c7' :
    status === 'overdue' ? '#fee2e2' :
    '#f3f4f6'};
  color: ${({ status }) =>
    status === 'paid' ? '#065f46' :
    status === 'pending' ? '#92400e' :
    status === 'overdue' ? '#991b1b' :
    '#6b7280'};
`;
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const Modal = styled.div`
  background: white;
  border-radius: 10px;
  width: 460px;
  max-width: calc(100% - 32px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.3);
`;
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eaeaea;
  h3 { margin: 0; font-size: 15px; font-weight: 600; }
`;
const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  &:hover { color: #1f2937; }
`;
const ModalBody = styled.div`
  padding: 18px 20px;
`;
const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid #eaeaea;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 12px;
`;
const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
`;
const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  font-size: 14px;
  outline: none;
  &:focus { border-color: var(--accent); }
`;
const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  font-size: 14px;
  background: white;
  outline: none;
`;
const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  margin-top: 4px;
`;
const NoticeBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
  border-radius: 5px;
  font-size: 11px;
  margin-bottom: 14px;
`;
const ErrorBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #fee2e2;
  color: #991b1b;
  border-radius: 5px;
  font-size: 12px;
`;
const PrimaryBtn = styled.button`
  padding: 8px 18px;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #1d4ed8; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const SecondaryBtn = styled.button`
  padding: 8px 16px;
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
`;
const Toast = styled.div`
  position: fixed;
  top: 80px;
  right: 30px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  background: ${({ kind }) => kind === 'error' ? '#fee2e2' : '#d1fae5'};
  color: ${({ kind }) => kind === 'error' ? '#991b1b' : '#065f46'};
  border: 1px solid ${({ kind }) => kind === 'error' ? '#fca5a5' : '#86efac'};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  font-size: 14px;
  font-weight: 500;
  z-index: 9999;
`;
